import { Box, CircularProgress, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { ArrowForward, FilterAlt, Inventory2, Search } from "@mui/icons-material";
import { MonthTile } from "../api/inventory";
import { EstimateState } from "./useFilterEstimate";

interface Props {
  selectedMonths: string[];
  // Per-month tiles, already aggregated across the selected corpora
  // (one entry per month; rollup_n_rows summed). null until loaded.
  inventory: MonthTile[] | null;
  filter: string;
  search: string;
  // Which wizard step the user is currently on (0-based: Months,
  // Filter, Search, Output, Review). Used to highlight the active
  // funnel node.
  step: number;
  // Estimate state owned by ExportNew (kicked by the Estimate button
  // on the Filter step or auto-kicked when the user clicks Next from
  // the Filter step). The funnel only renders.
  estimate: EstimateState;
  // True when the estimate is for an older filter/months key than
  // what's currently typed — the funnel renders the count dimmed and
  // hints "(stale)".
  estimateStale: boolean;
}

// Persistent breadcrumb-style funnel above the wizard's step body.
// Three nodes — Months, Filter, Search — narrow the candidate set
// from the full inventory population down to whatever lands in S3.
//
// Numbers:
//  - Months: sum of inventory rollup_n_rows over selected months.
//  - Filter: from the wizard-level estimate state. Shows "click
//    Estimate to refresh" until the user kicks one.
//  - Search: upper-bounded by the filter result; exact count isn't
//    knowable until the export actually runs (Lucene post-filter
//    happens at fetch time).

const fmt = (n: number) => n.toLocaleString();

interface NodeProps {
  active: boolean;
  done: boolean;
  icon: React.ReactNode;
  label: string;
  primary: string;
  secondary?: string;
  tooltip?: string;
  dim?: boolean;
}

function FunnelNode({
  active,
  done,
  icon,
  label,
  primary,
  secondary,
  tooltip,
  dim,
}: NodeProps) {
  const node = (
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1.25,
        flex: 1,
        minWidth: 0,
        borderColor: active
          ? "primary.main"
          : done
            ? "success.main"
            : "divider",
        borderWidth: active ? 2 : 1,
        opacity: dim ? 0.55 : !done && !active ? 0.6 : 1,
        transition: "border-color 120ms, opacity 120ms",
      }}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <Box
          sx={{
            color: active
              ? "primary.main"
              : done
                ? "success.main"
                : "text.disabled",
            display: "flex",
          }}
        >
          {icon}
        </Box>
        <Box minWidth={0} flexGrow={1}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", lineHeight: 1.2 }}
          >
            {label}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{ lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {primary}
          </Typography>
          {secondary && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {secondary}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
  return tooltip ? <Tooltip title={tooltip}>{node}</Tooltip> : node;
}

export function WizardFunnel({
  selectedMonths,
  inventory,
  filter,
  search,
  step,
  estimate,
  estimateStale,
}: Props) {
  // Months node — derived from the inventory directly.
  const monthsDocs = (() => {
    if (!inventory) return null;
    const idx = new Map(
      inventory.map((r) => [`${r.year}-${r.month}`, r.rollup_n_rows ?? 0]),
    );
    return selectedMonths.reduce((acc, m) => acc + (idx.get(m) ?? 0), 0);
  })();
  const monthsPrimary =
    selectedMonths.length === 0
      ? "—"
      : monthsDocs === null
        ? "loading…"
        : `${fmt(monthsDocs)} docs`;
  const monthsSecondary =
    selectedMonths.length === 0
      ? "no months selected"
      : `${selectedMonths.length} month${selectedMonths.length === 1 ? "" : "s"}`;

  // Filter node — driven by the explicit estimate state from the
  // wizard. No auto-kick; the user clicks Estimate or Next.
  const hasFilter = filter.trim().length > 0;
  const filterPrimary = (() => {
    if (!hasFilter) {
      return selectedMonths.length === 0 ? "—" : "no filter (passes all)";
    }
    if (estimate.kind === "idle") return "click Estimate";
    if (estimate.kind === "running") {
      const p = estimate.progress;
      // Progress shape: as soon as any sub-month has landed, surface
      // the partial total — the user sees a number that grows. Until
      // the first one lands we show the M-of-N counter alone.
      if (p && p.total > 0) {
        if (p.partialTotal > 0 && p.completed > 0) {
          return `${fmt(p.partialTotal)} so far · ${p.completed}/${p.total} months`;
        }
        return `${p.completed}/${p.total} months`;
      }
      return "estimating…";
    }
    if (estimate.kind === "ok") return `${fmt(estimate.total)} docs`;
    return "(estimate failed)";
  })();
  const filterSecondaryParts: string[] = [];
  if (hasFilter) {
    const expr =
      filter.trim().length > 32
        ? filter.trim().slice(0, 30) + "…"
        : filter.trim();
    filterSecondaryParts.push(expr);
  }
  if (estimate.kind === "ok" && monthsDocs && monthsDocs > 0) {
    const pct = (estimate.total / monthsDocs) * 100;
    filterSecondaryParts.push(`${pct.toFixed(pct < 1 ? 2 : 0)}% of inputs`);
  }
  if (estimate.kind === "ok" && estimateStale) {
    filterSecondaryParts.push("stale");
  }
  const filterSecondary = filterSecondaryParts.join(" · ") || undefined;
  const filterDim = estimate.kind === "ok" && estimateStale;

  // Search node — upper bound only.
  const knownUpperBound =
    estimate.kind === "ok" && !estimateStale
      ? estimate.total
      : !filter.trim() && monthsDocs !== null
        ? monthsDocs
        : null;
  const searchPrimary = search.trim()
    ? knownUpperBound !== null
      ? `≤ ${fmt(knownUpperBound)} docs`
      : "≤ filter result"
    : "no search (passes all)";
  const searchSecondary = search.trim()
    ? search.trim().length > 32
      ? search.trim().slice(0, 30) + "…"
      : search.trim()
    : "applied at export time";

  return (
    <Stack direction="row" alignItems="stretch" gap={1} sx={{ mb: 3 }}>
      <FunnelNode
        active={step === 0}
        done={step > 0 && selectedMonths.length > 0}
        icon={<Inventory2 fontSize="small" />}
        label="Months"
        primary={monthsPrimary}
        secondary={monthsSecondary}
        tooltip="Selected months × rollup row counts. The starting population for the export."
      />
      <Box sx={{ display: "flex", alignItems: "center", color: "text.disabled" }}>
        <ArrowForward fontSize="small" />
      </Box>
      <FunnelNode
        active={step === 1}
        done={
          step > 1 && hasFilter && estimate.kind === "ok" && !estimateStale
        }
        icon={
          estimate.kind === "running" ? (
            <CircularProgress size={16} />
          ) : (
            <FilterAlt fontSize="small" />
          )
        }
        label="Filter"
        primary={filterPrimary}
        secondary={filterSecondary}
        dim={filterDim}
        tooltip={
          estimate.kind === "error"
            ? `Estimate failed: ${estimate.message}`
            : "Metadata predicate run via DuckDB pushdown. Narrows the population before any per-page work."
        }
      />
      <Box sx={{ display: "flex", alignItems: "center", color: "text.disabled" }}>
        <ArrowForward fontSize="small" />
      </Box>
      <FunnelNode
        active={step === 2}
        done={step > 2}
        icon={<Search fontSize="small" />}
        label="Search"
        primary={searchPrimary}
        secondary={searchSecondary}
        tooltip="Optional Lucene post-filter on transcript text. Runs at export time after each page is fetched — exact count isn't knowable until then."
      />
    </Stack>
  );
}
